import { useState, useEffect } from "react";

import {
  DashboardFilterType,
  GetDashboardBidSummaryContent,
} from "@/types/dashboard";

import { formatKoreanCurrencyUnits, formatToPercentString } from "@/libs/utils";

import {
  DefaultDashbboardBidSummary,
  getDashboardBidFilterToString,
} from "@/consts/Dashboard";

import { GetDashboardBidSummary } from "@/services/Dashboard";

import { showToast } from "@/utils/toastUtils";

import DashboardSummaryCard from "@/components/DashboardSummaryCard";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardBidSummary({
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<GetDashboardBidSummaryContent>(
    DefaultDashbboardBidSummary as GetDashboardBidSummaryContent
  );
  const getSummary = async (newFilter: DashboardFilterType) => {
    try {
      setIsLoading(true);
      const params = getDashboardBidFilterToString(newFilter);
      const result = await GetDashboardBidSummary(params);
      if (result.data.success) {
        setSummary(result.data.pods[0].content);
      } else {
        showToast({
          message: "요약 정보를 조회하는데 실패했습니다.",
          type: "error",
        });
      }
    } catch (e) {
      showToast({
        message: "요약 정보를 조회하는데 실패했습니다.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      getSummary(filter);
    }
  }, [searchReady]);

  if (isLoading) {
    return (
      <div className="h-32 flex gap-5 justify-start items-start">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex-1 h-full bg-white rounded-xl p-5 flex flex-col justify-between"
          >
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-24" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="h-32 flex gap-5 justify-start items-start ">
      <DashboardSummaryCard
        title="총 공고건수"
        content={summary.total_biddings}
      />
      <DashboardSummaryCard
        title="자사투찰건수"
        titleContent={
          <div className="min-w-16 text-right justify-center text-sm font-semibold">
            <span className="text-dark-red">
              {formatToPercentString(
                summary.company_bid_count / summary.total_biddings
              )}
            </span>
            &nbsp;
            <span className="text-gray-C">
              ({summary.company_bid_count}/{summary.total_biddings})
            </span>
          </div>
        }
        content={summary.company_bid_count}
      />
      <DashboardSummaryCard
        title="자사계약건수"
        titleContent={
          <div className="min-w-16 text-right justify-center text-sm font-semibold">
            <span className="text-dark-red">
              {formatToPercentString(
                summary.company_contract_count / summary.company_bid_count
              )}
            </span>
            &nbsp;
            <span className="text-gray-C">
              ({summary.company_contract_count}/{summary.company_bid_count})
            </span>
          </div>
        }
        content={summary.company_contract_count}
      />
      <DashboardSummaryCard
        title="시장 전체 계약금액"
        content={
          summary.total_contract_amount > 0
            ? formatKoreanCurrencyUnits(summary.total_contract_amount)
            : "0원"
        }
      />
      <DashboardSummaryCard
        title="자사 계약금액"
        content={
          summary.company_contract_amount > 0
            ? formatKoreanCurrencyUnits(summary.company_contract_amount)
            : "0원"
        }
      />
    </div>
  );
}
